import { defineStore } from 'pinia';
import { computed } from 'vue';
import useCommonStore from '@/common/store/pinia/common';
import { TaskName } from '@/common/constants/taskNames.constants';
import { BackendTask, TaskState } from '@schema';
import { HA_PENDING_TASKS } from '@/common/constants/ha.constants';
import { TASK_STATE_IS_IN_PROGRESS } from '@/common/constants/tasks.constants';
import { useNotificationsStore } from '@/common/store/pinia/notifications';

const useTasksStore = defineStore('tasks', () => {
  const notificationsStore = useNotificationsStore();

  const mapItem = (item: BackendTask) => ({
    ...item,
    id: item.task_id,
    pathVariables: item.args,
    payload: item.kwargs,
  });

  const commonStore = useCommonStore<BackendTask, ReturnType<typeof mapItem>>({
    getUrl: () => '/api/v2/tasks',
    plural: (data) => data,
    itemKey: 'task_id',
    mapItem,
  })();

  const {
    getItems,
    getItemByProps,
    changeItem,
  } = commonStore;

  // getters
  const getFailedTask = computed(() => (name: TaskName) => getItemByProps.value({ name, state: TaskState.FAILED }));
  const getRunningTask = computed(() => (name: TaskName) => getItemByProps.value({ name, state: TaskState.RUNNING }));
  const getRunningTasks = computed(() => (taskNames: TaskName[]) => taskNames.map(getRunningTask.value).filter(Boolean));
  const hasRunningTask = computed(() => (taskNames: TaskName[]) => taskNames.some(getRunningTask.value));
  const creatingS3Task = computed(() => getRunningTask.value(TaskName.S3_CREATE));
  const creatingFileStorageTask = computed(() => getRunningTask.value(TaskName.FILE_STORAGE_CLUSTER_CREATE));
  const nodesUpdateFileStorageTask = computed(() => getItems.value.find(
    (task) => (task.name === TaskName.FILE_STORAGE_NODE_ADD || task.name === TaskName.FILE_STORAGE_NODE_DELETE) && TASK_STATE_IS_IN_PROGRESS.includes(task.state)
  ));
  const installingUpdatesTask = computed(() => getRunningTask.value(TaskName.UPDATES_START));
  const reportingProblemTask = computed(() => getRunningTask.value(TaskName.REPORT_PROBLEM));
  const deletingComputeNetworkTask = computed(() => getRunningTask.value(TaskName.COMPUTE_NETWORK_DELETE));
  const creatingComputeNetworkTask = computed(() => getRunningTask.value(TaskName.COMPUTE_NETWORK_CREATE));
  const reconfiguringComputeTask = computed(() => getRunningTask.value(TaskName.COMPUTE_RECONFIGURE));
  const computeDeployingTask = computed(() => getRunningTask.value(TaskName.COMPUTE_DEPLOY));
  const vhsDeployingTask = computed(() => getRunningTask.value(TaskName.VHS_DEPLOY));
  const computeJoiningNodeTask = computed(() => getRunningTask.value(TaskName.COMPUTE_JOINING_NODE));
  const computeReleasingNodeTask = computed(() => getRunningTask.value(TaskName.COMPUTE_RELEASE_NODE));
  const haReconfiguringTask = computed(() => getRunningTask.value(TaskName.HA_RECONFIGURING));
  const disablingEncryptionTask = computed(() => getRunningTask.value(TaskName.ROLE_SET_ENCRYPTION_DISABLE));
  const enablingEncryptionTask = computed(() => getRunningTask.value(TaskName.ROLE_SET_ENCRYPTION_ENABLE));
  const networkMigrationTask = computed(() => getRunningTask.value(TaskName.NETWORK_MIGRATION));
  const haRunningTask = computed(() => getItems.value.find(
    (task) => HA_PENDING_TASKS.includes(task.name as TaskName) && [TaskState.RUNNING, TaskState.PAUSED].includes(task.state)
  ));
  const haDestroyingTask = computed(() => getItems.value.find(
    (task) => task.name === TaskName.HA_DELETING && [TaskState.RUNNING, TaskState.PAUSED].includes(task.state)
  ));
  const nodeBackingUpTask = computed(() => getItems.value.find(
    task => task.name === TaskName.MANAGEMENT_NODE_BACKUP && TASK_STATE_IS_IN_PROGRESS.includes(task.state)
  ));
  const creatingNfsTask = computed(() => getRunningTask.value(TaskName.NFS_NODE_ASSIGN));
  const failedAbgwDeploymentTask = computed(() => getFailedTask.value(TaskName.ABGW_STANDALONE_DEPLOYMENT));

  const ws: SocketEvents = {
    tasks(data: BackendTask) {
      changeItem(data);
      notificationsStore.processWsTask(data);
    }
  };

  return {
    ...commonStore,
    // getters
    getRunningTask,
    getRunningTasks,
    hasRunningTask,
    creatingS3Task,
    creatingFileStorageTask,
    nodesUpdateFileStorageTask,
    installingUpdatesTask,
    reportingProblemTask,
    deletingComputeNetworkTask,
    creatingComputeNetworkTask,
    reconfiguringComputeTask,
    computeDeployingTask,
    vhsDeployingTask,
    computeJoiningNodeTask,
    computeReleasingNodeTask,
    haReconfiguringTask,
    disablingEncryptionTask,
    enablingEncryptionTask,
    haRunningTask,
    haDestroyingTask,
    nodeBackingUpTask,
    creatingNfsTask,
    networkMigrationTask,
    failedAbgwDeploymentTask,
    // actions
    ws
  };
});

export default useTasksStore;
